import { ConfigWrapper } from "@msidentity/SISU/config/config-wrapper";
import { SmsActionType } from "@msidentity/SISU/model/proof";
import type { ICountryInfo } from "@msidentity/SISU/utilities/country-helper";
import type { ServerData } from "@msidentity/SISU/utilities/server-data";

export type IUsernameRecoveryConfig = {
  showChallenge: boolean;
  sendCodeInfo: {
    sendOttAction: SmsActionType;
    sendOttUrl: string;
  };
  phoneCountries: ICountryInfo[];
  defaultPhoneCountry: string;
  cancelUrl: string;
  successUrl: string;
  accountsForProofUrl: string;
  bypassClientProofValidationWithZero: boolean;
  allowProofBasedSignIn: boolean;
  signInPostRedirectUrl: string;
};

/**
 * Default config data for the Username Recovery flow
 */
export const defaultUsernameRecoveryConfig: IUsernameRecoveryConfig = {
  showChallenge: true,
  sendCodeInfo: {
    sendOttAction: SmsActionType.UsernameRecovery,
    sendOttUrl: "",
  },
  phoneCountries: [],
  defaultPhoneCountry: "US",
  cancelUrl: "", // TODO: re-use GlobalConfig instead
  successUrl: "",
  accountsForProofUrl: "",
  bypassClientProofValidationWithZero: false,
  allowProofBasedSignIn: false,
  signInPostRedirectUrl: "",
};

const configWrapper = new ConfigWrapper(defaultUsernameRecoveryConfig);

/**
 * The config wrapper that should be used to access UsernameRecovery config properties
 */
export default configWrapper;

/* ********* ServerData helpers ********** */

/**
 * Initialize the UsernameRecovery config object from ServerData
 * @param serverData The IDP-specific server data object that should be used to initialize the UsernameRecovery config
 */
export function initUsernameRecoveryConfig(serverData: ServerData) {
  const mappedServerData: Partial<typeof defaultUsernameRecoveryConfig> = {};

  if (serverData.oSendCodeInfo) {
    mappedServerData.sendCodeInfo = {
      sendOttAction: SmsActionType.UsernameRecovery,
      sendOttUrl: serverData.oSendCodeInfo.urlSendOtt || "",
    };
  }

  if (serverData.urlAccountsForProof) {
    mappedServerData.accountsForProofUrl = serverData.urlAccountsForProof;
  }

  if (serverData.arrPhoneCountryList) {
    mappedServerData.phoneCountries = serverData.arrPhoneCountryList;
  }

  if (serverData.sPrefSMSCountry) {
    mappedServerData.defaultPhoneCountry = serverData.sPrefSMSCountry;
  }

  if (serverData.urlCancel) {
    mappedServerData.cancelUrl = serverData.urlCancel;
  }

  if (serverData.urlSuccess) {
    mappedServerData.successUrl = serverData.urlSuccess;
  }

  if (serverData?.fBypassClientProofValidationWithZero !== undefined) {
    mappedServerData.bypassClientProofValidationWithZero =
      serverData.fBypassClientProofValidationWithZero;
  }

  if (serverData?.fHasSkippedUsernameRecoverySpeedbump !== undefined) {
    mappedServerData.allowProofBasedSignIn = serverData.fHasSkippedUsernameRecoverySpeedbump;
  }

  if (serverData?.urlPostSltToLogin !== undefined) {
    mappedServerData.signInPostRedirectUrl = serverData.urlPostSltToLogin;
  }

  configWrapper.initialize(mappedServerData);
}
